// Billing admin types. These mirror ztc-core's billing DTOs 1:1 (dates as ISO
// strings, money as integer cents) and are formatted at the display edge rather
// than mapped through a separate dollars layer. See ai.zerotoclaw.core.billing.

export type SubscriptionSummary = {
  readonly stripeSubscriptionId: string;
  readonly stripeCustomerId: string;
  readonly customerEmail: string | null;
  readonly tenantId: string | null;
  readonly status: string;
  readonly billingMode: string;
  readonly activeItemCount: number;
  readonly monthlyTotalCents: number;
  readonly yearlyTotalCents: number;
  readonly createdAt: string | null;
};

export type SubscriptionItem = {
  readonly stripeItemId: string;
  readonly itemCode: string;
  // Catalog display_name (what Stripe checkout/invoices show); null/absent
  // when the catalog row is gone -- fall back to billingItemLabel(itemCode).
  readonly displayName?: string | null;
  readonly quantity: number;
  readonly unitAmountCents: number | null;
  readonly currency: string | null;
  readonly interval: string | null;
  readonly currentPeriodEnd: string | null;
  readonly removedAt: string | null;
};

export type Subscription = {
  readonly stripeSubscriptionId: string;
  readonly stripeCustomerId: string;
  readonly tenantId: string | null;
  readonly purchaseRef: string | null;
  readonly status: string;
  readonly billingMode: string;
  readonly cancelAt: string | null;
  readonly items: SubscriptionItem[];
};

export type SlotEntitlement = {
  readonly tenantId: string;
  readonly tier: string | null;
  readonly includedSlots: number;
  // Admin-granted slots on top of the tier baseline. Optional: absent from
  // responses cached before it shipped.
  readonly bonusSlots?: number;
  readonly purchasedSlots: number;
  readonly inUse: number;
  readonly allowance: number;
  readonly remaining: number;
};

export type MutationResult = { readonly ok: true } | { readonly ok: false; readonly message: string };

// Customer-facing billing overview for one tenant (IP-352), mirroring
// ai.zerotoclaw.core.billing.TenantBillingSummary. No Stripe ids: the customer
// endpoints resolve the subscription server-side from the tenant.
export type TenantBillingSummary = {
  readonly tenantId: string;
  readonly hasSubscription: boolean;
  readonly status: string | null;
  readonly cancelAt: string | null;
  readonly tier: string | null;
  // Human tier label from tier_definition; render this, never the tier code.
  // Optional: absent in summaries cached before the field shipped.
  readonly tierDisplayName?: string | null;
  readonly includedSlots: number;
  // Admin-granted slots on top of the tier baseline (tenants.bonus_slots).
  // Usually 0; gets its own segment on the slot card when set, so the
  // breakdown still adds up to slotAllowance. Optional: absent from
  // summaries cached before it shipped.
  readonly bonusSlots?: number;
  readonly purchasedSlots: number;
  readonly slotsInUse: number;
  readonly slotAllowance: number;
  readonly slotsRemaining: number;
  // Coaching sessions: total = tier baseline, used = total - remaining. No
  // purchase path yet, so the tier baseline is the total (IP-353).
  readonly coachingTotal: number;
  readonly coachingUsed: number;
  readonly coachingRemaining: number;
  // Comp/admin exemption (IP-353): when true both the slot + coaching cards
  // render "Unlimited" and the numeric breakdowns above are not meaningful.
  readonly unlimited: boolean;
  // The base plan's billing cadence (month/year), null without a subscription.
  readonly planInterval: string | null;
  // The cadences a slot can be bought at right now, with the recurring price
  // each would bill (IP-389). Empty without a subscription or when no coherent
  // price is configured for the subscription's currency.
  readonly slotPrices: readonly SlotPrice[];
};

// One buyable slot cadence with its recurring price, mirroring
// ai.zerotoclaw.core.billing.SlotPriceView (IP-389).
export type SlotPrice = {
  readonly interval: string;
  readonly currency: string;
  readonly unitAmountCents: number;
};

// The cadence the slot picker preselects: the plan's own cadence when it's
// buyable (that keeps the pre-IP-389 behavior as the default), else the first
// offered one, else null (no priced cadences -- checkout falls back server-side).
export const defaultSlotCadence = (
  planInterval: string | null,
  prices: readonly SlotPrice[],
): string | null =>
  prices.find((p) => p.interval === planInterval)?.interval ?? prices[0]?.interval ?? null;

// Whole-percent saving of a yearly slot vs 12 monthly ones. Null unless both
// cadences are offered in the same currency and yearly is actually cheaper --
// a "save 0%" (or negative) badge would be worse than none.
export const yearlySlotSavingsPercent = (prices: readonly SlotPrice[]): number | null => {
  const month = prices.find((p) => p.interval === "month");
  const year = prices.find((p) => p.interval === "year");
  if (!month || !year || month.currency !== year.currency || month.unitAmountCents <= 0) return null;
  const percent = Math.round((1 - year.unitAmountCents / (12 * month.unitAmountCents)) * 100);
  return percent > 0 ? percent : null;
};

// Compact per-cadence price suffix ("/mo", "/yr"); falls back to the raw value.
const INTERVAL_SUFFIXES: Record<string, string> = { month: "/mo", year: "/yr" };
export const intervalSuffix = (interval: string): string =>
  INTERVAL_SUFFIXES[interval] ?? `/${interval}`;

// Tier catalog + per-tier price book (IP-350), mirroring ai.zerotoclaw.core.billing
// TierView / TierPricingView.
export type TierPricing = {
  readonly itemCode: string;
  readonly interval: string;
  readonly unitAmountCents: number;
  readonly currency: string;
  readonly included: boolean;
};

// Checkout copy a tier item carries (name + description shown on the Stripe
// checkout page), used to prefill the tier form.
export type TierItemCopy = {
  readonly displayName: string;
  readonly description: string | null;
};

export type Tier = {
  readonly tierCode: string;
  readonly displayName: string;
  readonly rank: number;
  readonly includedSlots: number;
  readonly includedCoachingSessions: number;
  readonly active: boolean;
  // Optional: absent in list payloads cached before the field shipped.
  readonly trialDays?: number;
  readonly pricing: TierPricing[];
  // Optional: absent in list payloads cached before the copy fields shipped.
  readonly planItem?: TierItemCopy | null;
  readonly setupFeeItem?: TierItemCopy | null;
};

// Item codes that grant a purchasable integration slot (mutable in the UI).
export const INTEGRATION_SLOT_ITEM_CODE = "integration-slot";

// The base subscription line; its price + interval are tier-scoped. Each tier
// owns its own plan item ("base-plan-<tier>") so its Stripe checkout carries a
// tier-specific name; the bare "base-plan" code is the retired shared item,
// still matched for old mirror rows.
export const BASE_PLAN_ITEM_CODE = "base-plan";
export const isPlanItem = (itemCode: string): boolean =>
  itemCode === BASE_PLAN_ITEM_CODE || itemCode.startsWith(`${BASE_PLAN_ITEM_CODE}-`);
export const planItemCodeFor = (tierCode: string): string => `${BASE_PLAN_ITEM_CODE}-${tierCode}`;

const ITEM_LABELS: Record<string, string> = {
  "base-plan": "Base Plan",
  "integration-slot": "Integration Slot",
  "setup-fee": "Setup Fee",
};

// The one-time setup fee, priced per tier/currency at interval='once'. Like
// the plan item, each tier owns its own row ("setup-fee-<tier>") so its
// checkout line carries a tier-specific label; the bare code is the retired
// shared item.
export const SETUP_FEE_ITEM_CODE = "setup-fee";
export const isSetupFeeItem = (itemCode: string): boolean =>
  itemCode === SETUP_FEE_ITEM_CODE || itemCode.startsWith(`${SETUP_FEE_ITEM_CODE}-`);
export const setupFeeItemCodeFor = (tierCode: string): string => `${SETUP_FEE_ITEM_CODE}-${tierCode}`;
export const ONCE_INTERVAL = "once";

// Human label for a catalog item_code; falls back to a title-cased slug.
export const billingItemLabel = (itemCode: string): string =>
  ITEM_LABELS[itemCode] ??
  itemCode
    .split("-")
    .map((part) => (part.length > 0 ? part[0]!.toUpperCase() + part.slice(1) : part))
    .join(" ");

// Billing intervals offered in the UI (tier_pricing keys + the subscription
// plan picker). Stripe also supports day/week; we only surface month/year.
export const BILLING_INTERVALS = ["month", "year"] as const;

const INTERVAL_LABELS: Record<string, string> = {
  day: "Daily",
  week: "Weekly",
  month: "Monthly",
  year: "Yearly",
  once: "One-time",
};

// Human label for a billing interval; falls back to the raw value.
export const formatInterval = (interval: string): string => INTERVAL_LABELS[interval] ?? interval;

// Currencies the price book + checkout support. Mirrors VALID_BILLING_CURRENCIES
// in ztc-core; the admin pricing grid sets a price per currency. AUD leads (our
// home currency) so it is the grid's default view and first toggle option.
export const BILLING_CURRENCIES = ["aud", "usd"] as const;
export type BillingCurrency = (typeof BILLING_CURRENCIES)[number];

// Short label for the currency toggle (e.g. "USD").
export const currencyLabel = (currency: string): string => currency.toUpperCase();

// Cents -> "$1,234.50". Null/undefined renders as a dash.
export const formatCentsUsd = (cents: number | null | undefined): string =>
  cents == null
    ? "—"
    : `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const lineTotalCents = (item: SubscriptionItem): number | null =>
  item.unitAmountCents == null ? null : item.unitAmountCents * item.quantity;

// Recurring total (cents) per billing interval. Stripe also allows day/week
// lines but we only sell month/year (BILLING_INTERVALS); anything else,
// including one-time lines, is ignored. Pass active items only (activeItems).
export type RecurringTotals = {
  readonly monthlyCents: number;
  readonly yearlyCents: number;
};

export const recurringTotals = (items: SubscriptionItem[]): RecurringTotals => {
  const totalFor = (interval: string): number =>
    items
      .filter((item) => item.interval === interval)
      .reduce((sum, item) => sum + (item.unitAmountCents ?? 0) * item.quantity, 0);
  return { monthlyCents: totalFor("month"), yearlyCents: totalFor("year") };
};

// Monthly-equivalent of all recurring lines (the MRR figure): monthly lines at
// face value plus yearly lines spread over 12 months, rounded to whole cents.
export const mrrCents = ({ monthlyCents, yearlyCents }: RecurringTotals): number =>
  Math.round(monthlyCents + yearlyCents / 12);

// The real billing cadence behind an MRR figure, rendered alongside it whenever
// the blended number differs from what the card is actually charged. Null when
// the subscription is purely monthly (the MRR is the real monthly charge).
export const mrrBreakdown = ({ monthlyCents, yearlyCents }: RecurringTotals): string | null => {
  if (yearlyCents === 0) return null;
  if (monthlyCents === 0) return `billed ${formatCentsUsd(yearlyCents)}/yr`;
  return `${formatCentsUsd(monthlyCents)}/mo + ${formatCentsUsd(yearlyCents)}/yr`;
};

export type BillingTone = "ok" | "warn" | "crit" | "muted";

// Maps a Stripe subscription status to a display tone (shared by the status
// badge and the KPI tile).
export const subscriptionStatusTone = (status: string): BillingTone => {
  switch (status) {
    case "active":
    case "trialing":
      return "ok";
    case "past_due":
    case "unpaid":
    case "incomplete":
      return "warn";
    case "canceled":
    case "incomplete_expired":
      return "crit";
    default:
      return "muted";
  }
};

export const activeItems = (subscription: Subscription): SubscriptionItem[] =>
  subscription.items.filter((item) => item.removedAt === null);

// Base URL for Stripe Dashboard deep links. Points at the live dashboard
// (IP-353 cutover); sandbox-era objects would need the /test dashboard, but
// the sandbox mirror rows are wiped before go-live so none survive here.
const STRIPE_DASHBOARD_BASE_URL = "https://dashboard.stripe.com";

// Deep link to a subscription in the Stripe Dashboard. No backend lookup: the
// subscription id is all Stripe needs.
export const stripeDashboardSubscriptionUrl = (subscriptionId: string): string =>
  `${STRIPE_DASHBOARD_BASE_URL}/subscriptions/${subscriptionId}`;
